// Promise-based client over the fake backend Web Worker.
// Screens call `api.call('games.list', { filter })` as if hitting a remote API,
// and subscribe to pushed events with `api.on('notification', ...)`.

import type {
  ArgsOf,
  EventPayloads,
  EventTopic,
  Method,
  ReturnOf,
  WorkerOutbound,
} from '@shared/protocol';

type Pending = {
  resolve: (data: unknown) => void;
  reject: (err: Error) => void;
  timer: ReturnType<typeof setTimeout>;
};

type Listener<T extends EventTopic> = (payload: EventPayloads[T]) => void;
type AnyListener = (payload: unknown) => void;

const REQUEST_TIMEOUT_MS = 10_000;

class ApiClient {
  private worker: Worker;
  private pending = new Map<string, Pending>();
  private seq = 0;
  private listeners = new Map<EventTopic, Set<AnyListener>>();

  constructor(worker: Worker) {
    this.worker = worker;
    this.worker.addEventListener('message', (ev: MessageEvent<WorkerOutbound>) =>
      this.onMessage(ev.data),
    );
  }

  call<M extends Method>(method: M, args: ArgsOf<M>): Promise<ReturnOf<M>> {
    const id = `r${++this.seq}`;
    return new Promise<ReturnOf<M>>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending.delete(id);
        reject(new Error(`Zaman aşımı: ${method}`));
      }, REQUEST_TIMEOUT_MS);
      this.pending.set(id, { resolve: resolve as (d: unknown) => void, reject, timer });
      this.worker.postMessage({ kind: 'request', id, method, args });
    });
  }

  on<T extends EventTopic>(topic: T, listener: Listener<T>): () => void {
    let set = this.listeners.get(topic);
    if (!set) {
      set = new Set<AnyListener>();
      this.listeners.set(topic, set);
    }
    const wrapped = listener as AnyListener;
    set.add(wrapped);
    return () => set!.delete(wrapped);
  }

  private onMessage(msg: WorkerOutbound): void {
    if (msg.kind === 'response') {
      const p = this.pending.get(msg.id);
      if (!p) return;
      this.pending.delete(msg.id);
      clearTimeout(p.timer);
      if (msg.ok) p.resolve(msg.data);
      else p.reject(new Error(msg.error?.message ?? 'Bilinmeyen hata'));
      return;
    }
    if (msg.kind === 'event') {
      const set = this.listeners.get(msg.topic);
      if (set) for (const l of set) l(msg.payload);
    }
  }
}

// Single shared instance. The worker is bundled as a sibling of app.js at the site root.
const worker = new Worker('/worker.js', { type: 'module' });
export const api = new ApiClient(worker);
